import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import MetaTags from '../components/MetaTags'
import BookingBanner from '../components/BookingBanner'
import { getAssetUrl } from '../lib/utils'

const CATEGORIES = [
  'Alle',
  'Følelsesmæssig balance',
  'Børn & unge',
  'Vaner & livsstil',
  'Krop & sind',
  'Øvrige områder',
]

export default function TreatmentsOverviewPage() {
  const [treatments, setTreatments] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('Alle')
  const [sortBy, setSortBy] = useState('order') // 'order' or 'alpha'

  useEffect(() => {
    async function loadTreatments() {
      try {
        setLoading(true)
        const res = await fetch('/_api/public/content/treatments')
        if (res.ok) {
          const data = await res.json()
          const items = Array.isArray(data) ? data : data.items || []
          setTreatments(items)
        }
      } catch (err) {
        console.error('Fejl ved hentning af behandlinger:', err)
      } finally {
        setLoading(false)
      }
    }
    loadTreatments()
  }, [])

  // Filter and sort treatments
  const filteredTreatments = useMemo(() => {
    let list = [...treatments]

    if (selectedCategory !== 'Alle') {
      list = list.filter((t) => t.category_name?.toLowerCase() === selectedCategory.toLowerCase())
    }

    if (sortBy === 'alpha') {
      list.sort((a, b) => a.title.localeCompare(b.title, 'da'))
    } else {
      list.sort((a, b) => (a.order || 99) - (b.order || 99))
    }

    return list
  }, [treatments, selectedCategory, sortBy])

  return (
    <>
      <MetaTags
        title="Behandlinger - Trine Rosendal"
        description="Samlet oversigt over behandlingsområder hos Trine Rosendal i Holstebro. Få hjælp med hypnose, hypnoterapi og healing til børn, unge og voksne."
        canonicalUrl="https://trine-rosendal.dk/behandlinger"
      />

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8 sm:py-14 space-y-12">
        {/* Header Introduction */}
        <div className="max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D6E2CB] bg-[#EEF3E8] px-3.5 py-1 text-xs font-medium text-[#4C5E43]">
            <i className="fa-solid fa-seedling text-[11px] text-[#556B48]" aria-hidden="true" />
            <span>Individuelle behandlingsforløb</span>
          </div>

          <h1 className="font-serif text-3.5xl sm:text-4xl lg:text-5xl font-medium text-[#222E1D] tracking-tight">
            Behandlingsoversigt
          </h1>

          <p className="text-base sm:text-lg text-[#4D5E46] leading-relaxed">
            Hver behandling tilpasses individuelt. Uanset om du søger hjælp til angst, rygestop, vægttab, søvnproblemer eller hjælp til dit barn, mødes du med respekt, ro og professionel vejledning.
          </p>

          {/* Quick links to primary treatment modalities */}
          <div className="flex flex-wrap items-center gap-2 pt-2 text-xs">
            <span className="text-[#5C6E52] font-medium">Behandlingsform:</span>
            <Link
              to="/hypnose"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#D6E2CB] bg-[#F7F9F3] px-3.5 py-1.5 font-medium text-[#222E1D] hover:bg-[#E2EBD9] transition"
            >
              <i className="fa-solid fa-brain text-xs text-[#556B48]" aria-hidden="true" />
              <span>Læs om Hypnoterapi</span>
            </Link>
          </div>
        </div>

        {/* Filter & View Controls */}
        <div className="rounded-2xl border border-[#D6E2CB] bg-[#F7F9F3] p-4 sm:p-6 shadow-xs space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <span className="text-xs uppercase tracking-widest text-[#556B48] font-medium">
              Vælg behandlingsområde
            </span>

            {/* Sorting toggle */}
            <div className="flex items-center gap-2 self-start sm:self-auto text-xs text-[#5C6E52]">
              <span className="font-medium">Sortering:</span>
              <button
                type="button"
                onClick={() => setSortBy('order')}
                className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                  sortBy === 'order'
                    ? 'bg-[#E2EBD9] text-[#1E2818] font-semibold'
                    : 'text-[#5C6E52] hover:bg-[#EAF1E4]'
                }`}
              >
                Anbefalet
              </button>
              <button
                type="button"
                onClick={() => setSortBy('alpha')}
                className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                  sortBy === 'alpha'
                    ? 'bg-[#E2EBD9] text-[#1E2818] font-semibold'
                    : 'text-[#5C6E52] hover:bg-[#EAF1E4]'
                }`}
              >
                Alfabetisk (A-Å)
              </button>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-[#D6E2CB]">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition cursor-pointer ${
                  selectedCategory === cat
                    ? 'bg-[#5C7A4F] text-white shadow-xs font-semibold'
                    : 'bg-[#EAF1E4] text-[#3D5234] hover:bg-[#D6E2CB]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Treatment Cards Grid */}
        {loading ? (
          <div className="py-16 text-center text-sm text-[#556B48] animate-pulse">
            Indlæser behandlinger...
          </div>
        ) : filteredTreatments.length === 0 ? (
          <div className="rounded-2xl border border-[#DFE6D5] bg-[#F6F8F3] p-12 text-center text-[#685263] space-y-3">
            <p className="font-serif text-xl font-medium text-[#2B3824]">
              Ingen behandlinger matcher din søgning
            </p>
            <p className="text-sm">
              Prøv at søge efter noget andet eller nulstil dine filtre.
            </p>
            <button
              type="button"
              onClick={() => {
                setSelectedCategory('Alle')
                setSearchQuery('')
              }}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#E7EEDF] px-4 py-2 text-xs font-medium text-[#3E4E36] hover:bg-[#DCE7D3]"
            >
              Nulstil filtre
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTreatments.map((t) => {
              const slug = typeof t.slug === 'object' ? t.slug?._ : t.slug
              const imgUrl = Array.isArray(t.hero_image) ? t.hero_image[0] : t.hero_image

              return (
                <article
                  key={t.id || slug}
                  className={`group flex flex-col justify-between overflow-hidden rounded-2xl border shadow-xs transition duration-300 hover:-translate-y-1 hover:shadow-lg ${
                    slug === 'ring-og-spoerg'
                      ? 'border-2 border-[#ECC572] bg-gradient-to-br from-[#FFFDF7] to-[#FEF8E8] ring-1 ring-[#ECC572]/40'
                      : 'border border-[#D6E2CB] bg-[#F7F9F3] hover:border-[#F5D154]/70'
                  }`}
                >
                  <div>
                    {imgUrl && (
                      <div className="aspect-16/10 w-full overflow-hidden bg-[#D6E2CB]">
                        <img
                          src={getAssetUrl(imgUrl, { width: 600, quality: 80 })}
                          alt={t.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = imgUrl
                          }}
                        />
                      </div>
                    )}
                    <div className="p-6 space-y-2.5">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7E62]">
                        {t.category_name || 'Behandling'}
                      </span>
                      <h2 className="font-serif text-xl sm:text-2xl font-medium text-[#222E1D] group-hover:text-[#5c7a4f] transition-colors">
                        {t.title}
                      </h2>
                      <p className="text-xs sm:text-sm text-[#4D5E46] leading-relaxed line-clamp-4">
                        {t.short_description}
                      </p>
                    </div>
                  </div>

                  <div className="p-6 pt-0 border-t border-[#DFE7D6] mt-4 flex items-center justify-between">
                    <Link
                      to={`/behandlinger/${slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5C7A4F] group-hover:text-[#222E1D] transition-colors"
                    >
                      <span>{slug === 'ring-og-spoerg' ? 'Læs mere her' : 'Læs om behandlingen'}</span>
                      <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                    </Link>
                    {slug === 'ring-og-spoerg' ? (
                      <a
                        href="tel:42510105"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7A4B05] bg-[#FEEEC7] px-2.5 py-1 rounded-full hover:bg-[#FDE5A3] transition"
                      >
                        <i className="fa-solid fa-phone text-[10px]" aria-hidden="true" />
                        <span>Ring 42 51 01 05</span>
                      </a>
                    ) : (
                      <a
                        href="https://system.easypractice.net/book/online-booking-16"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] font-medium text-[#5C6E52] hover:text-[#222E1D] underline"
                      >
                        Book tid
                      </a>
                    )}
                  </div>
                </article>
              )
            })}
          </div>
        )}

        {/* Reassurance Booking Banner */}
        <BookingBanner
          title="Vil du høre, hvordan jeg kan hjælpe dig?"
          description="Du er altid velkommen til at kontakte mig for en uforpligtende snak om dine eller dit barns udfordringer, eller booke en tid direkte i online kalenderen."
        />
      </div>
    </>
  )
}
